import React from 'react';
import { ArrowUp, ArrowDown, Info } from 'lucide-react';
import { useNavigate } from 'react-router-dom';
interface TimeSeriesPoint {
  date: string;
  value: number;
}
interface SignalChartProps {
  data: {
    id: number;
    title: string;
    type: 'bar' | 'line';
    timeSeriesData: TimeSeriesPoint[];
    subtitle: string;
    trend: 'up' | 'down' | 'stable';
    percentage: number;
    timeframe: string;
    actionType?: string;
  };
}
const SignalChart: React.FC<SignalChartProps> = ({
  data
}) => {
  const navigate = useNavigate();
  const maxValue = Math.max(...data.timeSeriesData.map(point => point.value)) * 1.2; // Add 20% padding
  const handleTitleClick = () => {
    navigate(`/actions/${data.id}`);
  };
  const renderChart = () => {
    if (data.type === 'line') {
      return renderLineChart();
    }
    return renderBarChart(); // Default to bar chart
  };
  const renderBarChart = () => {
    return <div className="w-full h-48 flex flex-col" data-id="element-185">
        <div className="flex-1 flex items-end" data-id="element-186">
          {data.timeSeriesData.map((point, index) => {
          const height = point.value / maxValue * 100;
          return <div key={index} className="flex flex-col items-center flex-1" data-id="element-187">
                <div className="w-4/5 bg-[#0070d2] rounded-t" style={{
              height: `${height}%`
            }} data-id="element-188"></div>
                <div className="text-xs text-gray-500 mt-1 truncate w-full text-center" data-id="element-189">
                  {point.date}
                </div>
              </div>;
        })}
        </div>
      </div>;
  };
  const renderLineChart = () => {
    const points = data.timeSeriesData;
    const totalPoints = points.length;
    if (totalPoints < 2) return <div className="h-48 flex items-center justify-center text-gray-500" data-id="element-190">
          Not enough data
        </div>;
    // Calculate points for the SVG path
    const chartWidth = 100;
    const chartHeight = 50;
    const xStep = chartWidth / (totalPoints - 1);
    let pathD = `M 0,${chartHeight - points[0].value / maxValue * chartHeight}`;
    for (let i = 1; i < totalPoints; i++) {
      const x = i * xStep;
      const y = chartHeight - points[i].value / maxValue * chartHeight;
      pathD += ` L ${x},${y}`;
    }
    return <div className="w-full h-48 flex items-center justify-center" data-id="element-191">
        <svg viewBox={`0 0 ${chartWidth} ${chartHeight}`} className="w-full h-36 mt-2" data-id="element-192">
          {/* X-axis */}
          <line x1="0" y1={chartHeight} x2={chartWidth} y2={chartHeight} stroke="#E5E7EB" strokeWidth="0.5" data-id="element-193" />
          {/* Y-axis */}
          <line x1="0" y1="0" x2="0" y2={chartHeight} stroke="#E5E7EB" strokeWidth="0.5" data-id="element-194" />
          {/* Data line */}
          <path d={pathD} fill="none" stroke="#0070d2" strokeWidth="2" data-id="element-195" />
          {/* Data points */}
          {points.map((point, i) => <circle key={i} cx={i * xStep} cy={chartHeight - point.value / maxValue * chartHeight} r="1.5" fill="#0070d2" data-id="element-196" />)}
        </svg>
        <div className="absolute bottom-4 w-full px-4" data-id="element-197">
          <div className="flex justify-between text-xs text-gray-500" data-id="element-198">
            {points.map((point, i) => <div key={i} className="text-center truncate" style={{
            width: `${100 / totalPoints}%`
          }} data-id="element-199">
                {point.date}
              </div>)}
          </div>
        </div>
      </div>;
  };
  return <div className="bg-white border border-gray-200 rounded-md shadow-sm p-4" data-id="element-200">
      <div className="flex justify-between items-start mb-2" data-id="element-201">
        <div data-id="element-202">
          <h3 className="text-sm font-medium text-gray-900 hover:text-[#0070d2] cursor-pointer" onClick={handleTitleClick} data-id="element-203">
            {data.title}
          </h3>
          <p className="text-xs text-gray-500" data-id="element-204">
            {data.subtitle}
            {data.actionType && <span className="text-xs text-blue-500 ml-1" data-id="element-205">
                ({data.actionType})
              </span>}
          </p>
        </div>
        <div className="flex items-center" data-id="element-206">
          <div className={`flex items-center ${data.trend === 'up' ? 'text-green-600' : data.trend === 'down' ? 'text-red-600' : 'text-gray-500'}`} data-id="element-207">
            {data.trend === 'up' ? <ArrowUp size={14} data-id="element-208" /> : data.trend === 'down' ? <ArrowDown size={14} data-id="element-209" /> : null}
            <span className="text-xs ml-1" data-id="element-210">{data.percentage}%</span>
          </div>
          <div className="text-xs text-gray-500 ml-1" data-id="element-211">{data.timeframe}</div>
          <button className="ml-2 text-gray-400 hover:text-gray-600" data-id="element-212">
            <Info size={14} data-id="element-213" />
          </button>
        </div>
      </div>
      <div onClick={handleTitleClick} className="cursor-pointer" data-id="element-214">
        {renderChart()}
      </div>
    </div>;
};
export default SignalChart;